// Top-level navigation to /v1/auth/google/start (the OIDC flow returns
// a 302 to Google's authorize URL, so this must be a real navigation,
// not a fetch). The callback at /v1/auth/google/callback sets cookies
// server-side and redirects to the post-login destination.
//
// `from` (optional) is the post-login destination the page wants to
// resume — e.g. /activate?token=… when the user reached sign-in via a
// boat-claim QR. It's appended as ?from=… so /start can stash it in the
// signed state cookie and the callback can return there. The server
// re-validates it against a strict same-origin-path allowlist
// (validateFromRedirect), so a bad value here is simply ignored — it
// can't produce an open redirect.

export function GoogleSignInButton({
  label = 'Continue with Google',
  from,
  returnTo,
  remember,
  onClick,
}: {
  label?: string;
  from?: string;
  // activation_return_link: the boat origin to return to after activation. The
  // server re-validates it (validateReturnTo) at /start, so a bad value is
  // simply ignored.
  returnTo?: string | null;
  // "Keep me signed in" ticked on the sign-in card (task remember_me). Sent as
  // ?remember=1 so /start can stash the intent in the SIGNED state cookie;
  // the callback reads it from there, never from its own query string.
  // Undefined on the surfaces with no such control (signup, reveal), which
  // get the 24-hour default.
  remember?: boolean;
  // Runs synchronously before the navigation — the anonymous reveal uses it
  // to stash its resume state (sessionStorage) so the OIDC round trip can't
  // lose it.
  onClick?: () => void;
}) {
  const params = new URLSearchParams();
  if (from !== undefined && from.length > 0) params.set('from', from);
  if (returnTo !== undefined && returnTo !== null && returnTo.length > 0) {
    params.set('return_to', returnTo);
  }
  if (remember === true) params.set('remember', '1');
  const qs = params.toString();
  const href = qs.length > 0 ? `/v1/auth/google/start?${qs}` : '/v1/auth/google/start';
  return (
    <a href={href} className="btn btn-oauth" onClick={onClick}>
      <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <path
          fill="#4285F4"
          d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        />
      </svg>
      <span>{label}</span>
    </a>
  );
}
